import Link from "next/link"
import React, { useMemo, useState } from "react"
import { BorderedButtonAnchor } from "src/components/Button/BorderedButton"
import { formatDate } from "src/components/List/List"
import { useProposalsContractSWR } from "src/hooks/useContractsSWR"
import { useAdoptedMovingApplicationSWR } from "src/hooks/useAdoptedMovingApplicationSWR"
import { useIsMCI } from "src/hooks/useIsMCI"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { useNotification } from "@smoothincorg/smooth-ui"
import { SMOOTH_SMOOTHER_API_URL } from "src/lib/constants"
import { V1_OPE_URL } from "src/lib/constants/value"
import { ModalEditableForm } from "../ModalEditableForm/ModalEditableForm"
import * as styles from "./Contract.css"
import dayjs from "dayjs"
import { useSWRConfig } from "swr"

export const Contract: React.FC<{ proposalId: number }> = ({ proposalId }) => {
  const { patchWithToken } = useSmoothApi()
  const { data, isValidating, mutate } = useProposalsContractSWR(proposalId)
  const {
    data: adoptedMovingApplication,
    mutate: mutateMovingApplication,
    url: movingAppUrl,
  } = useAdoptedMovingApplicationSWR(proposalId)
  const { showAlert, showSnackbar } = useNotification()
  const { mutate: globalMutate } = useSWRConfig()

  // Proposal固有のキャッシュキーを厳密に管理
  const contractCacheKey = `${SMOOTH_SMOOTHER_API_URL}/proposals/${proposalId}/contract`
  const movingAppCacheKey = movingAppUrl // hookから取得した正確なURL

  const resourceName = `Proposal.${proposalId}.Contract`

  // 賃貸借契約締結日の編集状態管理
  const [isEditingLeaseAgreement, setIsEditingLeaseAgreement] = useState(false)
  const [agreedAtValue, setAgreedAtValue] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [justUpdated, setJustUpdated] = useState(false) // 更新直後フラグ

  const { mciNotionDocument } = useIsMCI(proposalId)

  const mciText = useMemo(() => {
    if (mciNotionDocument === null) return "読み込み中…"
    return mciNotionDocument ? "✅" : "❌"
  }, [mciNotionDocument])

  // 賃貸借契約締結日の編集モード切り替え
  const toggleLeaseAgreementEditMode = () => {
    if (!isEditingLeaseAgreement) {
      // Contractデータから取得し、なければMovingApplicationから取得
      const currentValue = data?.agreedAt || adoptedMovingApplication?.agreedAt
      if (currentValue) {
        setAgreedAtValue(dayjs(currentValue).format("YYYY-MM-DD"))
      } else {
        setAgreedAtValue("")
      }
    }
    setIsEditingLeaseAgreement(!isEditingLeaseAgreement)
  }

  // 賃貸借契約締結日の更新
  const updateLeaseAgreement = async () => {
    if (!adoptedMovingApplication?.id) {
      showAlert("更新に必要な情報が不足しています")
      return
    }

    if (!agreedAtValue.trim()) {
      showAlert("賃貸借契約締結日を入力してください")
      return
    }

    setIsSaving(true)

    const payload = {
      moving_application: {
        agreed_at: agreedAtValue,
      },
    }

    try {
      // lease_agreement以外にも影響するため確実に再描画させる
      const optimisticContractData = data ? { ...data, agreedAt: agreedAtValue } : null
      const optimisticMovingAppData = adoptedMovingApplication
        ? { ...adoptedMovingApplication, agreedAt: agreedAtValue }
        : null

      if (optimisticContractData) {
        mutate(optimisticContractData, false)
      }
      if (optimisticMovingAppData) {
        mutateMovingApplication(optimisticMovingAppData, false)
      }

      await patchWithToken(
        `${SMOOTH_SMOOTHER_API_URL}/proposals/${proposalId}/moving_applications/${adoptedMovingApplication.id}/lease_agreement`,
        payload
      )

      showSnackbar("賃貸借契約締結日を更新しました", "success")
      setIsEditingLeaseAgreement(false)
      setJustUpdated(true)

      // API更新完了後、確実にキャッシュ無効化を実行
      if (movingAppCacheKey) {
        await globalMutate(movingAppCacheKey, undefined, {
          revalidate: true,
          populateCache: false, // 他のProposalキャッシュに影響しない
        })
      }

      await globalMutate(contractCacheKey, undefined, {
        revalidate: true,
        populateCache: false,
      })

      await mutateMovingApplication(undefined, { revalidate: true })
      await mutate(undefined, { revalidate: true })

      // キャッシュ更新完了したので再描画
      setJustUpdated(false)
    } catch (error) {
      showAlert("更新に失敗しました")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className={styles.root}>
      <h2 className={styles.title}>
        契約
        {/* <BorderedButton
          onClick={(e) => {
            e.preventDefault()
            setEditingId((c) => {
              if (c === 0) return null
              return 0
            })
          }}
          style={{ marginLeft: '1rem', background: '#ffffff' }}
        >
          新規作成
        </BorderedButton> */}
        <BorderedButtonAnchor
          href={`${V1_OPE_URL}/admin/contracts/new?proposal_id=${proposalId}`}
          style={{
            marginLeft: "1rem",
            background: "#ffffff",
            display: "inline-block",
            fontWeight: "normal",
          }}
        >
          新規作成
        </BorderedButtonAnchor>
      </h2>
      <section>
        <div className={styles.movingApplication}>
          {data ? (
            <ModalEditableForm
              resourceName={resourceName}
              items={[
                [
                  "id",
                  <Link
                    className="underline "
                    key={proposalId}
                    href={`/admin/proposals/${proposalId}/contract`}
                  >
                    詳細を見る
                  </Link>,
                ],
                ["分割回数", data.numberOfPayment],
                ["interestRate", data.interestRate],
                ["interestAmount", data.interestAmount],
                ["譲渡対象かどうか", mciText],
                ["契約日", formatDate(data.contractedOn)],
                ["paidAmount", data.paidAmount],
                ["createdAt", formatDate(data.createdAt)],
                [
                  "賃貸借契約締結日",
                  isEditingLeaseAgreement ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input
                        type="date"
                        value={agreedAtValue}
                        onChange={(e) => setAgreedAtValue(e.target.value)}
                        style={{
                          padding: "4px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          fontSize: "14px",
                        }}
                      />
                      <button
                        onClick={updateLeaseAgreement}
                        disabled={isSaving}
                        style={{
                          padding: "4px 8px",
                          backgroundColor: isSaving ? "#6c757d" : "#007bff",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: isSaving ? "not-allowed" : "pointer",
                          fontSize: "12px",
                        }}
                      >
                        {isSaving ? "保存中..." : "保存"}
                      </button>
                      <button
                        onClick={() => setIsEditingLeaseAgreement(false)}
                        style={{
                          padding: "4px 8px",
                          backgroundColor: "#6c757d",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        キャンセル
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span>
                        {(() => {
                          // 更新直後はMovingApplicationのデータを優先表示
                          const agreedAtValue = justUpdated
                            ? adoptedMovingApplication?.agreedAt || data?.agreedAt
                            : data?.agreedAt || adoptedMovingApplication?.agreedAt

                          return agreedAtValue
                            ? dayjs(agreedAtValue).format("YYYY/MM/DD")
                            : "未設定"
                        })()}
                      </span>
                      <button
                        onClick={toggleLeaseAgreementEditMode}
                        style={{
                          padding: "2px 6px",
                          backgroundColor: "#f8f9fa",
                          border: "1px solid #dee2e6",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        編集
                      </button>
                    </div>
                  ),
                ],
              ]}
              opeHref={`/admin/proposals/${proposalId}/contract`}
              temporary_EDIT_IN_SMOOTHER_OPE_URL={`${V1_OPE_URL}/admin/contracts/${data?.id}/edit`}
              resource={data}
              onSubmit={null}
            />
          ) : isValidating ? (
            <div>読み込み中…</div>
          ) : (
            <div>契約がありません</div>
          )}
        </div>
      </section>
    </div>
  )
}
